import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";
import ProfileEditor from "./ProfileEditor";

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-neutral-200 p-6">
      <div className="overline text-neutral-500">{label}</div>
      <div className="font-serif text-4xl mt-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

function Overview() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/referrer/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const refLink = `${window.location.origin}/?ref=${user?.user_id}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(refLink); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed"); }
  };

  if (!stats) return <div className="overline text-neutral-400">Loading...</div>;

  return (
    <div data-testid="referrer-overview">
      <h1 className="font-serif text-4xl font-light tracking-tight mb-2">{t.referrer.title}</h1>
      <p className="text-sm text-neutral-500 mb-8">{t.referrer.subtitle}</p>

      <div className="bg-black text-white p-6 lg:p-8 mb-8" data-testid="referrer-link-card">
        <div className="overline text-neutral-400 mb-3">{t.referrer.yourLink}</div>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <code className="flex-1 text-xs sm:text-sm font-mono break-all bg-white/5 px-4 py-3 select-all" data-testid="referrer-link">{refLink}</code>
          <button onClick={copy} className="bg-white text-black px-5 py-3 overline hover:bg-neutral-200 transition-colors flex items-center gap-2 whitespace-nowrap" data-testid="copy-link">
            {copied ? <><Check size={14} /> {t.referrer.copied}</> : <><Copy size={14} /> {t.referrer.copy}</>}
          </button>
        </div>
        <p className="mt-4 text-xs text-neutral-400 leading-relaxed">{t.referrer.linkHint}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={t.referrer.clicks} value={stats.clicks} />
        <StatCard label={t.referrer.salesAttributed} value={stats.orders_count} sub={`${stats.units_sold} units`} />
        <StatCard label={t.referrer.salesVolume} value={`€${stats.sales_total.toFixed(2)}`} />
        <StatCard label={t.referrer.commission} value={`€${stats.commission_earned.toFixed(2)}`} sub="10% of attributed sales" />
      </div>

      <div className="mt-10">
        <h2 className="font-serif text-2xl font-light mb-4">{t.referrer.recentOrders}</h2>
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="brutal-table w-full">
            <thead>
              <tr><th>ID</th><th>Date</th><th>Items</th><th>Total</th><th>{t.referrer.yourCommission}</th></tr>
            </thead>
            <tbody>
              {stats.recent_orders.map((o) => {
                const myComm = o.items.reduce((s, i) => s + (i.referrer_fee || 0) * i.quantity, 0);
                return (
                  <tr key={o.id} data-testid={`ref-order-${o.id}`}>
                    <td className="font-mono text-xs">{o.id.slice(0, 8)}</td>
                    <td className="text-xs text-neutral-500">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="text-xs">{o.items.map((i) => `${i.title} ×${i.quantity}`).join(", ")}</td>
                    <td>€{o.total.toFixed(2)}</td>
                    <td className="text-emerald-700 font-medium">€{myComm.toFixed(2)}</td>
                  </tr>
                );
              })}
              {stats.recent_orders.length === 0 && <tr><td colSpan="5" className="text-center py-12 overline text-neutral-400">—</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function ReferrerDashboard() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  if (loading) return <div className="min-h-screen flex items-center justify-center overline text-neutral-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "referrer") return <Navigate to="/" replace />;

  const links = [
    { to: "/referrer", end: true, label: t.referrer.overview, key: "overview" },
    { to: "/referrer/profile", label: t.profile.title, key: "profile" },
  ];

  return (
    <Routes>
      <Route element={<DashboardLayout role="referrer" links={links} />}>
        <Route index element={<Overview />} />
        <Route path="profile" element={<ProfileEditor />} />
      </Route>
    </Routes>
  );
}
